import * as React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import logger from 'js/app/loggerSingleton';
import Retracked from 'js/app/retracked';

import type { ModalName } from '@coursera/event-pulse-types';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import CancelDeflectionModal from 'bundles/my-purchases-graphql/components/modals/CancelDeflectionModal';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import type SubscriptionNextBillDetailsV1 from 'bundles/naptimejs/resources/subscriptionNextBillDetails.v1';
import TrackedButton from 'bundles/page/components/TrackedButton';
import type { ProductType } from 'bundles/payments/common/ProductType';
import ProductTypeObject from 'bundles/payments/common/ProductType';
import Modal from 'bundles/phoenix/components/Modal';
import type { NonTerminalSurveyData, SurveyData } from 'bundles/refund/common/types';
import BlockCertinFreeTrialCancelSubscriptionModal from 'bundles/refund/components/BlockCertInFreeTrialCancelSubscriptionModal';
import RefundReasonCheckboxContent from 'bundles/refund/components/RefundReasonCheckboxContent';
import RefundReasonMultipleChoiceContent from 'bundles/refund/components/RefundReasonMultipleChoiceContent';
import RefundReasonShortAnswerContent from 'bundles/refund/components/RefundReasonShortAnswerContent';
import steps from 'bundles/refund/constants/steps';
import type { StepType } from 'bundles/refund/constants/steps';
import {
  createSurvey,
  getDeflectionOffers,
  getPropertiesForDeflection,
  updateSurvey,
} from 'bundles/refund/utils/apiUtils';
import type { CourseraPlusUpgradeAction, DeflectionProps } from 'bundles/refund/utils/apiUtils';
import { QuestionTypes } from 'bundles/survey-form/constants/SurveyFormConstants';
import type SurveyResponseActionType from 'bundles/survey-form/constants/SurveyResponseActionType';

import _t from 'i18n!nls/refund';

import 'css!./__styles__/RefundModal';

// Tracking data goes all the way down into js/lib/retracked.js, which is not typed yet.
// Typing that file is beyond the scope of this diff.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TrackingData = any;

type PropsToFC = {
  modalTrackingName?: string;
  refundModalClass?: string;
  eventingModalName?: ModalName;
  isLoading: boolean;
  step: StepType;
  productItemId?: string;
  handleCloseModal: () => void;
  handleContinue: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
  title: React.ReactNode;
  body: React.ReactNode;
  footer: React.ReactNode;
  isFreeTrial?: boolean;
  productType?: ProductType;
  subscriptionId?: string;
  subscriptionStartsAt?: number;
};

const RefundModalFC = ({
  modalTrackingName,
  refundModalClass,
  eventingModalName = 'refund',
  isLoading,
  step,
  productItemId,
  handleCloseModal,
  handleContinue,
  title,
  body,
  footer,
  isFreeTrial,
  productType,
  subscriptionId,
  subscriptionStartsAt,
}: PropsToFC) => {
  const componentClassNames = classNames(refundModalClass, {
    'cancel-deflection': step === steps.cancelDeflection,
  });

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_modal', {
    modal: {
      name: eventingModalName,
    },
    ...(productItemId
      ? {
          product: {
            id: productItemId,
          },
        }
      : {}),
  });

  if (
    (productType === ProductTypeObject.COURSERA_PLUS_SUBSCRIPTION ||
      productType === ProductTypeObject.SPECIALIZATION_SUBSCRIPTION) &&
    step === steps.request &&
    isFreeTrial
  ) {
    return (
      <BlockCertinFreeTrialCancelSubscriptionModal
        handleClose={handleCloseModal}
        handleContinue={handleContinue}
        subscriptionId={subscriptionId}
        productType={productType}
        productItemId={productItemId}
        isLoading={isLoading}
      />
    );
  }

  return (
    <div className={componentClassNames}>
      <Modal
        modalName="Refund modal"
        handleClose={handleCloseModal}
        trackingName={modalTrackingName}
        shouldFocusOnXButton={true}
      >
        {isLoading ? (
          <div className="c-img-loading" />
        ) : (
          <div className="title-body-container" ref={visibilityTrackingRef}>
            <div className="title modal-container">
              <h3 className="modal-title center">{title}</h3>
            </div>
            <div className="content modal-container">
              {body}
              {footer}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export type Props = {
  title: React.ReactNode;
  requestDescription?: React.ReactNode | string;
  confirmationTitle?: React.ReactNode | string;
  confirmationDescription?: React.ReactNode | string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  onError: () => void;
  surveyResponseActionType: keyof typeof SurveyResponseActionType;
  productType?: ProductType;
  productItemId?: string;
  subscriptionId?: string;
  subscriptionStartsAt?: number;
  modalTrackingName?: string;
  closeButtonTrackingName?: string;
  confirmButtonTrackingName?: string;
  closeButtonText?: string;
  confirmButtonText?: string;
  useCloseAsMainCta?: boolean;
  mainCtaClass?: string;
  secondaryCtaClass?: string;
  footerClass?: string;
  trackingData?: TrackingData;
  refundModalClass?: string;
  isFreeTrial?: boolean;
  subscriptionNextBillDetails?: SubscriptionNextBillDetailsV1;
  eventingModalName?: ModalName;
};

type State = {
  step: StepType;
  isLoading: boolean;
  surveyData?: SurveyData;
  surveyDataForPreviousQuestions?: SurveyData[];
  currentSurveyQuestionIndex: number;
  answer?: string | Array<string>;
  deflectionPromoCode?: string;
  deflectionUpsell?: CourseraPlusUpgradeAction;
};

class RefundModal extends React.Component<Props, State> {
  static contextTypes = {
    _eventData: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof RefundModal.contextTypes>;

  static defaultProps = {
    modalTrackingName: 'refund_modal',
    closeButtonTrackingName: 'refund_close',
    confirmButtonTrackingName: 'refund_confirm',
    get closeButtonText() {
      return _t('Cancel');
    },
    get confirmButtonText() {
      return _t("Yes I'm sure");
    },
    mainCtaClass: 'primary cozy',
    secondaryCtaClass: 'secondary cozy',
    footerClass: 'footer',
    trackingData: {},
    refundModalClass: 'rc-RefundModal',
  };

  state = {
    step: steps.request,
    isLoading: false,
    surveyData: undefined,
    surveyDataForPreviousQuestions: [],
    currentSurveyQuestionIndex: 0,
    answer: undefined,
    deflectionPromoCode: undefined,
    deflectionUpsell: undefined,
  };

  async componentDidMount() {
    const { subscriptionId } = this.props;
    // Naptime 404s prevent a component from rendering. This may return a 404.
    if (subscriptionId) {
      getDeflectionOffers(subscriptionId).then((result) => {
        const deflectionProps: DeflectionProps = getPropertiesForDeflection(result);
        this.setState(() => deflectionProps);
      });
    }
  }

  handleContinue = (e: React.SyntheticEvent<HTMLButtonElement>): void => {
    const { step } = this.state;
    (e.target as HTMLButtonElement).blur(); // remove focus from the button after it is clicked

    switch (step) {
      case steps.confirmation:
        this.moveToNextStep();
        break;
      default:
        this.createOrUpdateSurveyResponse();
        break;
    }
  };

  handleCloseModal = (): void => {
    const { onClose } = this.props;
    onClose();
  };

  handleCloseUpsell = (): void => {
    this.handleCloseModal();
  };

  // TS issue with accessing directly from state which requires casting to `unknown`, then `SurveyData`
  getSurveyData(): SurveyData | undefined {
    return this.state.surveyData;
  }

  getContinueButtonProps = () => {
    const { mainCtaClass } = this.props;
    return {
      className: mainCtaClass,
      onClick: this.handleContinue,
    };
  };

  getSurveyDescription() {
    const { productType } = this.props;

    const {
      SPECIALIZATION,
      SPECIALIZATION_SUBSCRIPTION,
      VERIFIED_CERTIFICATE,
      COURSERA_PLUS_SUBSCRIPTION,
      COURSERA_TIER_LITE,
    } = ProductTypeObject;

    let productTypeName = null;
    switch (productType) {
      case COURSERA_PLUS_SUBSCRIPTION:
        productTypeName = 'Coursera Plus';
        break;
      case COURSERA_TIER_LITE:
        productTypeName = 'Coursera Lite';
        break;
      case SPECIALIZATION:
      case SPECIALIZATION_SUBSCRIPTION:
        productTypeName = _t('the Specialization');
        break;
      case VERIFIED_CERTIFICATE:
        productTypeName = _t('the course');
        break;
      default:
        logger.warn(`Unable to get survey description for product type ${productType}`);
    }

    if (productTypeName) {
      return _t('Would you like to take a quick survey about your experience with #{productTypeName}?', {
        productTypeName,
      });
    } else {
      return null;
    }
  }

  getIsCourseraLiteOrPlusSubscription = () => {
    const { productType } = this.props;
    return (
      productType === ProductTypeObject.COURSERA_PLUS_SUBSCRIPTION ||
      productType === ProductTypeObject.COURSERA_TIER_LITE
    );
  };

  createOrUpdateSurveyResponse() {
    const { productType, productItemId, subscriptionId, surveyResponseActionType } = this.props;
    const { answer, currentSurveyQuestionIndex, surveyDataForPreviousQuestions } = this.state;
    const surveyData = this.getSurveyData();
    const isCourseraLiteOrPlusSubscription = this.getIsCourseraLiteOrPlusSubscription();

    this.setState(() => ({ isLoading: true }));

    if (surveyData?.isTerminal || !productType) {
      this.moveToNextStep();
      return;
    }

    let request;

    if (this.isNonTerminalSurvey(surveyData)) {
      const { responseId, sectionId, questionId, questionType } = surveyData;

      // For now we want the back button only on Lite and Plus Subscription surveys
      if (isCourseraLiteOrPlusSubscription) {
        this.setState({
          surveyDataForPreviousQuestions: [...surveyDataForPreviousQuestions, surveyData],
          currentSurveyQuestionIndex: currentSurveyQuestionIndex + 1,
        });
      }

      request = updateSurvey({ responseId, sectionId, questionId, questionType, answer });
    } else {
      request = createSurvey({ productType, productItemId, subscriptionId, surveyResponseActionType });
    }

    request
      .then(this.setSurveyData)
      // skip to last survey step if API fails - user should not be blocked on refunding
      .catch(this.moveToNextStep);
  }

  moveToNextStep = () => {
    const { _eventData } = this.context;
    const { onConfirm, onClose, productType, productItemId } = this.props;
    const { step, deflectionPromoCode, deflectionUpsell } = this.state;
    const isDeflection = !!deflectionPromoCode || !!deflectionUpsell;
    const surveyData = this.getSurveyData();
    this.setState(() => ({ isLoading: false, answer: undefined }));

    if (step === steps.request) {
      if (isDeflection) {
        this.setState(() => ({ step: steps.cancelDeflection }));
      } else {
        this.setState(() => ({ isLoading: true }));
        onConfirm().then(() => {
          this.setState(() => ({
            step: steps.confirmation,
            isLoading: false,
          }));
          Retracked.trackComponent(_eventData, { productType, productItemId }, 'cancel_confirmation', 'view');
        });
      }
    } else if (this.isNonTerminalSurvey(surveyData)) {
      const { questionType } = surveyData;
      let nextStep: $TSFixMe;
      switch (questionType) {
        case QuestionTypes.MULTIPLE_CHOICE:
          nextStep = steps.reasonMultipleChoiceContent;
          break;
        case QuestionTypes.CHECKBOX:
          nextStep = steps.reasonCheckboxContent;
          break;
        case QuestionTypes.SHORT_ANSWER:
          nextStep = steps.reasonShortAnswerContent;
          break;
        default:
          onClose();
          break;
      }

      this.setState(() => ({ step: nextStep }));
    } else {
      onClose();
    }
  };

  updateStep = (step: StepType) => {
    this.setState(() => ({ step }));
  };

  setSurveyData = (surveyData: SurveyData): void => {
    this.setState(() => ({ surveyData }), this.moveToNextStep);
  };

  setSurveyDataForPreviousQuestions = () => {
    const { currentSurveyQuestionIndex } = this.state;
    const index = currentSurveyQuestionIndex > 0 ? currentSurveyQuestionIndex - 1 : currentSurveyQuestionIndex;
    this.setState(
      ({ surveyDataForPreviousQuestions }) => ({
        surveyData: surveyDataForPreviousQuestions?.[index],
        currentSurveyQuestionIndex: index,
      }),
      this.moveToNextStep
    );
  };

  setAnswer = (answer: string | Array<string>): void => {
    this.setState(() => ({ answer }));
  };

  setAnswerAndUpdateSurvey = (answer: string | Array<string>): void => {
    this.setState(
      () => ({
        answer,
      }),
      this.createOrUpdateSurveyResponse
    );
  };

  isNonTerminalSurvey(surveyData: $TSFixMe): surveyData is NonTerminalSurveyData {
    return surveyData && !surveyData.isTerminal;
  }

  renderTitle() {
    const { step } = this.state;
    const { title, confirmationTitle } = this.props;
    const surveyData = this.getSurveyData();

    switch (step) {
      case steps.request:
        return title;
      case steps.confirmation:
        return confirmationTitle ?? _t("You're all set!");
      case steps.cancelDeflection:
        return '';
      default:
        return this.isNonTerminalSurvey(surveyData) ? surveyData.title : _t('Before you go');
    }
  }

  renderSurveyPreviousQuestionButton() {
    const { trackingData, secondaryCtaClass } = this.props;
    return (
      <TrackedButton
        data-testid="go-to-previous-question-button"
        trackingName="go_to_previous_question_button"
        data={trackingData}
        className={secondaryCtaClass}
        onClick={this.setSurveyDataForPreviousQuestions}
      >
        {_t('Previous question')}
      </TrackedButton>
    );
  }

  renderBody() {
    const { requestDescription, footerClass, confirmationDescription, onConfirm, subscriptionId, onError } = this.props;

    const { step, deflectionPromoCode, currentSurveyQuestionIndex } = this.state;
    const surveyData = this.getSurveyData();
    const surveyOptions = this.isNonTerminalSurvey(surveyData) ? surveyData.options : null;
    const surveyStepDescription = this.isNonTerminalSurvey(surveyData)
      ? surveyData.prompt
      : _t('It would help Coursera if you provided your feedback.');

    switch (step) {
      case steps.request:
        return typeof requestDescription === 'string' ? <p>{requestDescription}</p> : requestDescription;
      case steps.cancelDeflection:
        if (deflectionPromoCode) {
          return (
            <CancelDeflectionModal
              promoCode={deflectionPromoCode}
              // @ts-expect-error Property 'updateStep' does not exist on type 'IntrinsicAttributes & IntrinsicClassAttributes<Component<NaptimeProps, any, any>> & Readonly<NaptimeProps> & Readonly<...>'. ts(2322)
              updateStep={this.updateStep}
              cancelSubscription={onConfirm}
              subscriptionId={subscriptionId}
              onError={onError}
            />
          );
        }
        return null; // the other modal (doing the upgrade) is seem
      case steps.confirmation:
        return (
          <div>
            <p>{confirmationDescription}</p>
            {!!surveyData && <p>{this.getSurveyDescription()}</p>}
          </div>
        );
      case steps.reasonMultipleChoiceContent:
        return (
          <RefundReasonMultipleChoiceContent
            setAnswer={this.setAnswerAndUpdateSurvey}
            description={surveyStepDescription}
            reasons={surveyOptions}
            surveyFooter={
              currentSurveyQuestionIndex > 0 ? (
                <div className={classNames(footerClass, 'cta-left-aligned')}>
                  {this.renderSurveyPreviousQuestionButton()}
                </div>
              ) : undefined
            }
          />
        );
      case steps.reasonCheckboxContent:
        return (
          <RefundReasonCheckboxContent
            setAnswer={this.setAnswer}
            description={surveyStepDescription}
            reasons={surveyOptions}
          />
        );
      case steps.reasonShortAnswerContent:
        return <RefundReasonShortAnswerContent setAnswer={this.setAnswer} description={surveyStepDescription} />;
      default:
        return <div />;
    }
  }

  renderRequestFooter() {
    const {
      closeButtonTrackingName,
      closeButtonText,
      confirmButtonTrackingName,
      confirmButtonText,
      useCloseAsMainCta,
      trackingData,
      mainCtaClass,
      secondaryCtaClass,
      footerClass,
    } = this.props;

    let closeButtonClassName = secondaryCtaClass;
    const continueButtonProps = this.getContinueButtonProps();

    if (useCloseAsMainCta) {
      continueButtonProps.className = secondaryCtaClass;
      closeButtonClassName = mainCtaClass;
    }

    const closeButton = (
      <TrackedButton
        trackingName={closeButtonTrackingName}
        data={trackingData}
        className={closeButtonClassName}
        onClick={this.handleCloseModal}
      >
        {closeButtonText}
      </TrackedButton>
    );
    const confirmButton = (
      <TrackedButton trackingName={confirmButtonTrackingName} data={trackingData} {...continueButtonProps}>
        {confirmButtonText}
      </TrackedButton>
    );

    if (useCloseAsMainCta) {
      return (
        <div className={footerClass}>
          {confirmButton}
          {closeButton}
        </div>
      );
    } else {
      return (
        <div className={footerClass}>
          {closeButton}
          {confirmButton}
        </div>
      );
    }
  }

  renderFooter() {
    const { closeButtonTrackingName, trackingData, footerClass, secondaryCtaClass } = this.props;
    const { step, surveyData, currentSurveyQuestionIndex } = this.state;
    const buttonProps = this.getContinueButtonProps();
    const isCourseraLiteOrPlusSubscription = this.getIsCourseraLiteOrPlusSubscription();

    switch (step) {
      case steps.request:
        return this.renderRequestFooter();
      case steps.confirmation:
        return surveyData ? (
          <div className={footerClass}>
            <TrackedButton
              trackingName={closeButtonTrackingName}
              data={trackingData}
              className={secondaryCtaClass}
              onClick={this.handleCloseModal}
              data-testid="refund-modal-steps-no-thanks-button"
            >
              {_t('No thanks')}
            </TrackedButton>
            <button type="button" {...buttonProps}>
              {isCourseraLiteOrPlusSubscription ? _t('Yes') : _t('Continue')}
            </button>
          </div>
        ) : (
          <div className={footerClass}>
            <button type="button" {...buttonProps}>
              {isCourseraLiteOrPlusSubscription ? _t('Close') : _t('OK')}
            </button>
          </div>
        );
      case steps.reasonCheckboxContent:
        return (
          <div className={footerClass}>
            <button type="button" {...buttonProps}>
              {_t('Next')}
            </button>
          </div>
        );
      case steps.reasonShortAnswerContent:
        return (
          <div className={classNames(footerClass, { 'cta-space-between': currentSurveyQuestionIndex > 0 })}>
            {currentSurveyQuestionIndex > 0 ? this.renderSurveyPreviousQuestionButton() : undefined}
            <button type="button" {...buttonProps}>
              {_t('Submit')}
            </button>
          </div>
        );
      default:
        return null;
    }
  }

  render() {
    const {
      modalTrackingName,
      refundModalClass,
      eventingModalName,
      productItemId,
      isFreeTrial,
      productType,
      subscriptionId,
      subscriptionStartsAt,
    } = this.props;
    const { isLoading, step } = this.state;
    return (
      <RefundModalFC
        modalTrackingName={modalTrackingName}
        refundModalClass={refundModalClass}
        eventingModalName={eventingModalName}
        isLoading={isLoading}
        step={step}
        productItemId={productItemId}
        subscriptionId={subscriptionId}
        subscriptionStartsAt={subscriptionStartsAt}
        productType={productType}
        handleCloseModal={this.handleCloseModal}
        title={this.renderTitle()}
        body={this.renderBody()}
        footer={this.renderFooter()}
        isFreeTrial={isFreeTrial}
        handleContinue={this.handleContinue}
      />
    );
  }
}

export default RefundModal;
